import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import contact from '../../data/contact';

const Contact = () => {
  const { lang, t } = useLanguage();

  return (
    <div className="py-12 space-y-16 animate-fade-in text-white max-w-6xl mx-auto">
      {/* Header */}
      <div className="space-y-4 pb-4 border-b border-white/10 text-center md:text-start">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/25 text-primary-300 text-xs font-semibold tracking-wide">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
          {lang === 'ar' ? 'متاح للاستشارات والأدوار القيادية' : 'Available for Advisory & Leadership Inquiries'}
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight">
          {t('sections.contactTitle')}
        </h1>
        <p className="text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed">
          {lang === 'ar'
            ? 'سواء كنت ترغب في مناقشة قيادة تقنية، معمارية بوابات الدفع، الأمن السيبراني، أو استشارة استراتيجية، يسعدني التواصل معك.'
            : 'Whether you want to discuss executive technology leadership, payment gateway architecture, cybersecurity governance, or strategic advisory, let’s connect.'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column (7 cols): Direct Communication & Email Hub */}
        <div className="lg:col-span-7 space-y-8">
          {/* Primary Email Card */}
          <div className="p-8 sm:p-10 rounded-[2.5rem] bg-gradient-to-br from-white/[0.06] via-white/[0.03] to-white/[0.015] border border-white/10 shadow-2xl space-y-6 group">
            <div className="space-y-2">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-400">
                {lang === 'ar' ? 'القناة المباشرة' : 'Direct Channel'}
              </span>
              <h2 className="text-2xl font-bold text-white">
                {lang === 'ar' ? 'البريد الإلكتروني الرسمي' : 'Official Direct Email'}
              </h2>
              <p className="text-sm text-white/75 leading-relaxed font-normal">
                {lang === 'ar'
                  ? 'أفضل وسيلة للتواصل المهني ومناقشة الفرص الاستراتيجية ومشاريع الأنظمة الحيوية.'
                  : 'The most direct channel for executive discussions, consulting engagements, and technical partnerships.'}
              </p>
            </div>

            {/* Clean Clickable Email Row (Without extra buttons) */}
            <a
              href={`mailto:${contact.email}`}
              className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-primary-500/40 hover:bg-white/[0.06] transition-all duration-300 flex items-center justify-between group/email"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary-500/15 border border-primary-500/25 flex items-center justify-center text-primary-400 shrink-0 group-hover/email:scale-105 transition-transform">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <span className="text-[11px] uppercase tracking-wider text-white/50 font-bold block">
                    {lang === 'ar' ? 'البريد الإلكتروني المباشر' : 'Direct Email Address'}
                  </span>
                  <span className="text-lg sm:text-xl font-bold text-white group-hover/email:text-primary-300 transition-colors">
                    {contact.email}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 text-white/40 group-hover/email:text-primary-300 transition-colors">
                <svg className="w-5 h-5 transform group-hover/email:translate-x-1 rtl:group-hover/email:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </div>
            </a>

            {/* Quick Context Chips */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-1">
                <div className="flex items-center gap-2 text-xs font-bold text-primary-300">
                  <svg className="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{t('sections.contactAvailabilityTitle')}</span>
                </div>
                <p className="text-xs text-white/70 leading-relaxed">
                  {lang === 'ar' ? 'استجابة سريعة في أقل من 24 ساعة خلال أيام العمل.' : 'Typically responsive within 24 hours on business days.'}
                </p>
              </div>

              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-1">
                <div className="flex items-center gap-2 text-xs font-bold text-secondary-300">
                  <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{t('sections.contactLocationTitle')}</span>
                </div>
                <p className="text-xs text-white/70 leading-relaxed">
                  {lang === 'ar' ? 'الجزائر العاصمة (UTC+1) • متوافق مع توقيت الخليج وأوروبا.' : 'Algiers, DZ (UTC+1) • Aligned with Gulf (GST) & Europe.'}
                </p>
              </div>
            </div>
          </div>

          {/* Advisory Scope Card */}
          <div className="p-8 rounded-[2.5rem] bg-white/[0.03] border border-white/10 space-y-4">
            <h3 className="text-lg font-bold text-white">
              {lang === 'ar' ? 'مجالات النقاش والاستشارة الرئيسية' : 'Key Areas of Engagement'}
            </h3>
            <ul className="space-y-3">
              {[
                lang === 'ar'
                  ? 'قيادة وإدارة الفرق التقنية ومراكز الموارد المشتركة (Group CTO/CSO).'
                  : 'Executive Technology Leadership & Shared Resource Hub Management (Group CTO/CSO).',
                lang === 'ar'
                  ? 'بناء بوابات الدفع الإلكتروني وهندسة المعاملات فائقة السرعة بلغة Rust و Laravel.'
                  : 'High-Concurrency Payment Gateway Architecture in Rust & Laravel.',
                lang === 'ar'
                  ? 'الامتثال التنظيمي للأنظمة المالية والصحية (PCI DSS v4.0.1، ISO 27001، ADHICS).'
                  : 'Regulatory Compliance & Audit Leadership (PCI DSS v4.0.1, ISO 27001, ADHICS).',
                lang === 'ar'
                  ? 'إدارة الأزمات، التعافي من الكوارث (DR) والترحيل السحابي الفوري تحت الضغط.'
                  : 'High-Load Crisis Management, Disaster Recovery (DR) & Zero-Downtime Cloud Migration.',
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm text-white/80 font-normal">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-400 mt-2 shrink-0"></span>
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right Column (5 cols): Verified Networks & Profiles */}
        <div className="lg:col-span-5 space-y-6">
          <div className="p-8 sm:p-10 rounded-[2.5rem] bg-white/[0.03] border border-white/10 space-y-6 h-full flex flex-col justify-between shadow-xl">
            <div className="space-y-2">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-400">
                {lang === 'ar' ? 'الملفات المهنية' : 'Professional Presence'}
              </span>
              <h2 className="text-xl sm:text-2xl font-bold text-white">
                {t('sections.contactSocialTitle')}
              </h2>
              <p className="text-sm text-white/70 leading-relaxed font-normal">
                {t('sections.contactSocialDesc')}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 pt-2">
              {/* LinkedIn */}
              <a
                href={contact.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-[#0077b5]/50 hover:bg-[#0077b5]/10 transition-all flex items-center justify-between group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#0077b5]/15 border border-[#0077b5]/30 flex items-center justify-center text-[#0077b5] group-hover:scale-105 transition-transform">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-white group-hover:text-primary-300 transition-colors">LinkedIn</h4>
                    <p className="text-xs text-white/60">Executive network & career updates</p>
                  </div>
                </div>
                <span className="text-white/40 group-hover:text-white transition-colors">↗</span>
              </a>

              {/* GitHub */}
              <a
                href={contact.github}
                target="_blank"
                rel="noopener noreferrer"
                className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-white/30 hover:bg-white/[0.06] transition-all flex items-center justify-between group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-white group-hover:scale-105 transition-transform">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-white group-hover:text-primary-300 transition-colors">GitHub</h4>
                    <p className="text-xs text-white/60">Open-source code, Trax & repositories</p>
                  </div>
                </div>
                <span className="text-white/40 group-hover:text-white transition-colors">↗</span>
              </a>

              {/* X / Twitter */}
              <a
                href="https://twitter.com/samirdjelal"
                target="_blank"
                rel="noopener noreferrer"
                className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-[#1da1f2]/50 hover:bg-[#1da1f2]/10 transition-all flex items-center justify-between group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#1da1f2]/15 border border-[#1da1f2]/30 flex items-center justify-center text-[#1da1f2] group-hover:scale-105 transition-transform">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.84 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-white group-hover:text-primary-300 transition-colors">X (Twitter)</h4>
                    <p className="text-xs text-white/60">Thoughts on systems, security & tech</p>
                  </div>
                </div>
                <span className="text-white/40 group-hover:text-white transition-colors">↗</span>
              </a>
            </div>

            <div className="pt-4 border-t border-white/10">
              <p className="text-xs text-white/50 text-center">
                {lang === 'ar' ? 'التشفير وحماية البيانات مضمونان لكافة الاتصالات.' : 'Confidentiality and prompt response guaranteed.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
